import { getUserOptions } from "@/api/auth";
import { ShinyIcon } from "@/assets/icons/ShinyIcon";
import ConnectButton from "@/components/connectButton/ConnectButton";
import {
	Avatar,
	Button,
	Container,
	Group,
	Menu,
	Paper,
	Skeleton,
	Tabs,
	Text,
	Title,
	UnstyledButton,
	useMantineTheme,
} from "@mantine/core";
import { useMediaQuery, useWindowScroll } from "@mantine/hooks";
import {
	IconChevronDown,
	IconLogout,
	IconMail,
	IconUserPin,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import cx from "clsx";
import { useState } from "react";
import classes from "./navbar.module.css";

const tabs = [
	"nfts",
	"file-upload",
	"create-metadata",
	"update-price",
	"mint-nft",
	"burn-nft",
];

function AdminBanner() {
	const auth = useQuery(getUserOptions());
	const { data: user } = auth;

	if (!user || user.roles.includes("admin")) {
		return null;
	}

	return (
		<Paper
			p="xs"
			bg="yellow.1"
			style={{
				borderRadius: 0,
				borderBottom: "1px solid var(--mantine-color-yellow-3)",
			}}
		>
			<Container size="xl">
				<Text c="yellow.9" size="sm" fw={500} ta="center">
					Admin privileges are required to mint NFTs and modify metadata. Please
					contact your administrator for access.
				</Text>
			</Container>
		</Paper>
	);
}

export function Navbar() {
	const navigate = useNavigate();
	const location = useLocation();
	const collection = location.pathname.split("/")[1];
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const [userMenuOpened, setUserMenuOpened] = useState(false);
	const [scroll] = useWindowScroll();

	const auth = useQuery(getUserOptions());

	const headerClass = cx(classes.header, { [classes.scrolled]: scroll.y > 10 });

	return (
		<div className={headerClass}>
			<AdminBanner />
			<Container className={classes.mainSection} size="xl">
				<Group justify="space-between">
					<Link
						to="/"
						style={{ textDecoration: "none" }}
						viewTransition={false}
					>
						<Group gap={5} align="center" my="xs">
							<ShinyIcon
								width={isMobile ? 30 : 40}
								height={isMobile ? 30 : 40}
							/>
							<Title order={isMobile ? 4 : 3}>Shiny Admin Dashboard</Title>
						</Group>
					</Link>
					{auth.isPending && <Skeleton width={100} height={30} />}
					<Group flex={1} justify={isMobile ? "space-between" : "right"}>
						{collection && <ConnectButton />}

						{!auth.isPending && !auth.data && (
							<Button
								onClick={() => {
									window.location.href = `${
										import.meta.env.VITE_API_URL
									}/auth/login`;
								}}
							>
								Log In
							</Button>
						)}

						{auth.data && (
							<Menu
								width={260}
								position="bottom-end"
								transitionProps={{ transition: "pop-top-right" }}
								onClose={() => setUserMenuOpened(false)}
								onOpen={() => setUserMenuOpened(true)}
								withinPortal
							>
								<Menu.Target>
									<UnstyledButton
										className={cx(classes.user, {
											[classes.userActive]: userMenuOpened,
										})}
									>
										<Group gap={7}>
											<Avatar
												src={auth.data.picture}
												alt={auth.data.name}
												radius="xl"
												size={20}
											/>
											{!isMobile && (
												<Text fw={500} size="sm" lh={1} mr={3}>
													{auth.data.name}
												</Text>
											)}
											<IconChevronDown size={12} stroke={1.5} />
										</Group>
									</UnstyledButton>
								</Menu.Target>
								<Menu.Dropdown>
									<Menu.Item
										leftSection={
											<IconMail
												size={16}
												color={theme.colors.teal[6]}
												stroke={1.5}
											/>
										}
									>
										{auth.data.email}
									</Menu.Item>
									{auth.data.roles.includes("admin") && (
										<Menu.Item
											leftSection={
												<IconUserPin
													size={16}
													color={theme.colors.blue[6]}
													stroke={1.5}
												/>
											}
										>
											Admin
										</Menu.Item>
									)}
									<Menu.Divider />
									<Menu.Item
										color="red"
										leftSection={<IconLogout size={16} stroke={1.5} />}
										onClick={() => {
											window.location.href = `${
												import.meta.env.VITE_API_URL
											}/auth/logout`;
										}}
									>
										Logout
									</Menu.Item>
								</Menu.Dropdown>
							</Menu>
						)}
					</Group>
				</Group>
			</Container>
			{collection?.startsWith("0x") && (
				<Container size="xl">
					<Tabs
						value={location.pathname.split("/")[2]}
						onChange={(value) => {
							navigate({
								to: `/${collection}/${value}`,
							});
						}}
						defaultValue={tabs[0]}
						variant="outline"
						classNames={{
							root: classes.tabs,
							list: classes.tabsList,
							tab: classes.tab,
						}}
					>
						<Tabs.List>
							{tabs.map((tab) => (
								<Tabs.Tab key={tab} value={tab}>
									{tab
										.split("-")
										.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
										.join(" ")
										.toUpperCase()}
								</Tabs.Tab>
							))}
						</Tabs.List>
					</Tabs>
				</Container>
			)}
		</div>
	);
}
